import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useApi } from 'hooks';
import { scrollTop } from 'utils';
import { makeStyles } from '@mui/styles';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import {
  UiCheckbox,
  UiFormControlLabel,
  UiToolbar,
  UiIcon,
  UiButton,
} from 'ui-components';
import Pagination from 'components/pagination/Pagination';
import Loading from 'components/loading/Loading';
import { useSelector } from 'react-redux';
import UiTableHead from './UiTableHead';
import UiTableActions from './UiTableActions';
import DisplayError from '../../components/error/DisplayError';
import { StyledUiTable } from './UiTable.styles';

const useStyles = makeStyles((theme) => ({
  root: {
    width: '100%',
  },
  container: {
    borderRadius: '8px',
  },
  paper: {
    width: '100%',
    marginBottom: theme.spacing(2),
    borderRadius: '8px',
  },
  table: {
    // minWidth: 750,
    fontFamily: 'inherit !important',
  },
  checkbox: {
    margin: '0 15px ',
  },
  visuallyHidden: {
    border: 0,
    clip: 'rect(0 0 0 0)',
    height: 1,
    margin: -1,
    overflow: 'hidden',
    padding: 0,
    position: 'absolute',
    top: 20,
    width: 1,
  },
}));

const UiTable = ({
  columns = [],
  columnButtons = [],
  actions = {},
  apiKey = '',
  children,
  title = '',
  apiParams = {},
  forceUpdate = 0,
  onRecivedData = () => {},
  disableCheckbox = false,
  emptyIcon,
  displayCheckbox = true,
  paginationType = 'paginate',
  paginationKey,
  'data-testid': dataTestId,
}) => {
  const { t } = useTranslation();
  const { data, error, loading, get, getApiParams, updateData } = useApi();
  const pageContentCountSlider = useSelector(
    (state) => state.settingsReducer.filters.public.pageContentCountSlider
  );
  const classes = useStyles();
  const [selected, setSelected] = useState([]);
  const [activeNextBtn, setActiveNextBtn] = useState(false);
  //صفحه انتخاب شده
  const [page, setPage] = useState(1);
  const { path, params, dataModel } = getApiParams(apiKey);
  const allParams = { ...params, ...apiParams };
  //محاسبه تعداد صفحه
  const pageCount = Math.ceil(
    data?.meta?.total /
      (allParams?.per_page || allParams?.count || allParams?.paginate)
  );

  useEffect(() => {
    get(path, { ...allParams, page }, dataModel, true).then((response) => {
      if (paginationKey) {
        setActiveNextBtn(response.meta[paginationKey]);
      }
      if (typeof onRecivedData === 'function') onRecivedData(response, page);
    });
    scrollTop();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [get, getApiParams, page, apiKey, forceUpdate]);

  useEffect(() => {
    setSelected([]);
    //درصورت تغییر دیتا به صورت کلاینتی اگر دیتا کمتر از قبل بود تعداد صفحه کم شه
    if (page > pageCount && pageCount !== 0) {
      setPage(page - 1);
    } else if (data?.data) {
      onRecivedData(data, page);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const handleSelectAllCheckbox = (event) => {
    if (event.target.checked) {
      const newSelecteds = data?.data;
      setSelected(newSelecteds);
      return;
    }
    setSelected([]);
  };

  const handleChangeCheckbox = (event, row) => {
    const selectedIndex = selected.findIndex((i) => i?.id === row.id);
    let newSelected = [];
    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, row);
    } else if (selectedIndex >= 0) {
      newSelected = selected.filter((i) => i.id !== row?.id);
    }
    setSelected(newSelected);
  };

  const handleChangePage = (newPage) => {
    setPage(newPage);
    setSelected([]);
  };

  //تشخیص این که سطر انتخاب شده یا نه
  const isSelected = (row) => selected.findIndex((i) => i?.id == row.id) !== -1;

  let contentList = (
    <div className='flex center h-70'>
      <Loading spinning={true} />
    </div>
  );

  if (loading) {
    contentList = (
      <div className='flex center h-70'>
        <Loading spinning={true} />
      </div>
    );
  } else if (error) {
    contentList = (
      <div className='flex center h-70'>
        <DisplayError error={error} />
      </div>
    );
  } else {
    contentList = (
      <Table
        stickyHeader
        className={classes.table}
        aria-labelledby='tableTitle'
        size={'medium'}
        aria-label='enhanced table'
        style={{
          minHeight:
            data?.data?.length > 0
              ? ((data?.data?.length ||
                  params?.per_page ||
                  params?.count ||
                  params?.paginate) +
                  1) *
                70
              : `${572}px`,
        }}
        data-testid={`${dataTestId}-table`}
      >
        {/* بخش هدر جدول */}
        <UiTableHead
          columns={columns}
          columnButtons={columnButtons}
          displayCheckbox={displayCheckbox}
        />
        {/* نمایش متن دیتا خالی */}
        {data?.data?.length === 0 && (
          <TableBody>
            <TableRow data-testid={`${dataTestId}-table-empty-row`}>
              <TableCell
                colSpan={columns?.length + 2}
                className='ui-table-empty-data'
              >
                <div className='flex center'>
                  {emptyIcon && (
                    <UiIcon
                      icon={emptyIcon}
                      size={25}
                      className='mb-18'
                      color='#CCD5DC'
                      data-testid={`${dataTestId}-empty-icon`}
                    />
                  )}
                </div>
                <div className='ui-table-empty-data-label'>
                  {t('common:emptyList', {
                    title,
                  })}
                </div>
              </TableCell>
            </TableRow>
          </TableBody>
        )}

        {/* نمایش دیتا جدول در صورت نبود لودینگ */}
        {data?.data && data?.data?.length > 0 && (
          <TableBody>
            {data?.data?.map((row, index) => {
              const isItemSelected = isSelected(row);
              const labelId = `enhanced-table-checkbox-${index}`;

              return (
                <TableRow
                  hover
                  role='checkbox'
                  aria-checked={isItemSelected}
                  tabIndex={-1}
                  key={row.id}
                  selected={isItemSelected}
                  data-testid={`${dataTestId}-table-row`}
                >
                  <TableCell
                    padding={displayCheckbox ? 'checkbox' : 'none'}
                    align='right'
                    className='checkbox-column'
                  >
                    {displayCheckbox && (
                      <UiCheckbox
                        disabled={disableCheckbox}
                        onChange={(event) => handleChangeCheckbox(event, row)}
                        checked={isItemSelected}
                        inputProps={{
                          'aria-labelledby': labelId,
                          'data-testid': `${dataTestId}-item-checkbox`,
                        }}
                        className={classes.checkbox}
                      />
                    )}
                  </TableCell>

                  {columns.map((column, columnIndex) => {
                    const value = row[column?.key];
                    return (
                      <TableCell
                        key={column?.key || columnIndex}
                        align='right'
                        style={{ width: column?.width, padding: 0 }}
                        className={column?.className}
                      >
                        {column.render ? column.render(value, row) : value}
                      </TableCell>
                    );
                  })}

                  {/* اپیدت کردن دیتا بعد از انتخاب اکشن با updateData  */}

                  <UiTableActions
                    row={row}
                    actions={actions}
                    updateData={updateData}
                    data-testid={`${dataTestId}-table-actions`}
                  />
                </TableRow>
              );
            })}
          </TableBody>
        )}
      </Table>
    );
  }

  return (
    <StyledUiTable className={`${classes.root} ui-table`}>
      {displayCheckbox && (
        <div className='ui-table-options'>
          <UiFormControlLabel
            label={t('common:selectAll')}
            control={
              <UiCheckbox
                disabled={disableCheckbox}
                indeterminate={
                  selected.length > 0 && selected.length < data?.data?.length
                }
                checked={
                  data?.data?.length > 0 &&
                  selected.length === data?.data?.length
                }
                onChange={handleSelectAllCheckbox}
                inputProps={{
                  'aria-label': 'select all desserts',
                  'data-testid': `${dataTestId}-table-select-all`,
                }}
                className={classes.checkbox}
              />
            }
          />
          {children}
        </div>
      )}

      <Paper className={classes.paper}>
        <TableContainer className={classes.container}>
          {contentList}
        </TableContainer>
      </Paper>
      {/* پجینیشن جدول */}
      {paginationType === 'paginate' &&
        data?.meta?.total > pageContentCountSlider.selectedValue && (
          <Pagination
            total={pageCount * pageContentCountSlider.selectedValue}
            currentPage={page}
            onPageChange={handleChangePage}
            dataTestid={`${dataTestId}-pagination`}
          />
        )}
      {paginationType === 'next-back' && (
        <div className='flex between'>
          <UiButton
            startIcon={
              <UiIcon
                icon='ri-arrow-right-s-line'
                color={`${activeNextBtn}?"#CCD5DC":"#199DA3"`}
              />
            }
            variant='text'
            className='cancel-button'
            disabled={page <= 1}
            onClick={() => setPage((x) => x - 1)}
            data-testid={`${dataTestId}-table-before-btn`}
          >
            {t('common:previousPage')}
          </UiButton>
          <UiButton
            endIcon={
              <UiIcon
                icon='ri-arrow-left-s-line'
                color={`${activeNextBtn}?"#CCD5DC":"#199DA3"`}
              />
            }
            variant='text'
            className='cancel-button'
            disabled={activeNextBtn}
            onClick={() => setPage((x) => x + 1)}
            data-testid={`${dataTestId}-table-after-btn`}
          >
            {t('common:nextPage')}
          </UiButton>
        </div>
      )}
      {/* قسمت تولبار برای اکشن روی  مالتی سلکت */}
      {selected?.length > 0 && (
        <UiToolbar
          selected={selected}
          title={title}
          actions={actions?.toolbar || []}
          updateData={updateData}
          onRemoveSelected={() => setSelected([])}
          data-testid={`${dataTestId}-table-toolbar`}
        />
      )}
    </StyledUiTable>
  );
};

UiTable.propTypes = {
  columns: PropTypes.array.isRequired,
  columnButtons: PropTypes.array,
  actions: PropTypes.object.isRequired,
  apiKey: PropTypes.string.isRequired,
  children: PropTypes.node,
  title: PropTypes.string,
  emptyIcon: PropTypes.string,
  paginationKey: PropTypes.string,
  apiParams: PropTypes.object,
  forceUpdate: PropTypes.number,
  onRecivedData: PropTypes.func,
  disableCheckbox: PropTypes.bool,
  displayCheckbox: PropTypes.bool,
  paginationType: PropTypes.oneOf(['paginate', 'scroll', 'next-back']),
  'data-testid': PropTypes.string,
};

export default UiTable;
